import type { ReactNode } from "react";
import { Box, Stack, Text } from "@mantine/core";

type MantineTextSize = "xs" | "sm" | "md";

export type SarahHelpMarkdownProps = {
  content: string;
  color?: string;
  size?: MantineTextSize;
};

/** Inline: **bold**, *italic*, `code`, and plain email mailto links — no HTML from the model. */
function linkEmails(text: string, keyPrefix: string): ReactNode[] {
  const re = /\b([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})\b/gi;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const email = match[1]!;
    nodes.push(
      <Box
        component="a"
        key={`${keyPrefix}-em-${i++}`}
        href={`mailto:${email}`}
        style={{ color: "#0369A1", fontWeight: 600, textDecoration: "underline" }}
      >
        {email}
      </Box>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length ? nodes : [text];
}

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`|\*([^*\n]+)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  const pushPlain = (plain: string) => {
    nodes.push(...linkEmails(plain, `p-${key++}`));
  };

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushPlain(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      nodes.push(
        <Box component="strong" key={`b-${key++}`} style={{ fontWeight: 700 }}>
          {linkEmails(match[1], `b-${key}`)}
        </Box>,
      );
    } else if (match[2] !== undefined) {
      nodes.push(
        <Box
          component="code"
          key={`c-${key++}`}
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: "0.92em",
            padding: "1px 4px",
            borderRadius: 4,
            background: "rgba(15, 23, 42, 0.06)",
          }}
        >
          {match[2]}
        </Box>,
      );
    } else if (match[3] !== undefined) {
      nodes.push(
        <Box component="em" key={`i-${key++}`} style={{ fontStyle: "italic" }}>
          {match[3]}
        </Box>,
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    pushPlain(text.slice(lastIndex));
  }

  return nodes.length ? nodes : [text];
}

/**
 * Lightweight markdown for Sarah help replies (newlines, bold, lists).
 * Does not parse HTML — safe for untrusted LLM text.
 */
export function SarahHelpMarkdown({
  content,
  color = "#0F172A",
  size = "sm",
}: SarahHelpMarkdownProps) {
  const lines = String(content ?? "")
    .replace(/\r\n/g, "\n")
    .split("\n");

  return (
    <Stack gap={4}>
      {lines.map((line, i) => {
        if (line.trim() === "") {
          return <Box key={`gap-${i}`} h={6} />;
        }

        const ordered = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
        if (ordered) {
          const [, indent, num, rest] = ordered;
          const pad = Math.min((indent?.length ?? 0) * 4, 24);
          return (
            <Text
              key={`ol-${i}`}
              size={size}
              c={color}
              component="div"
              style={{
                lineHeight: 1.55,
                paddingLeft: pad,
                display: "flex",
                gap: 6,
              }}
            >
              <Box component="span" style={{ flexShrink: 0, minWidth: 18, fontWeight: 600 }}>
                {num}.
              </Box>
              <Box component="span" style={{ flex: 1 }}>
                {renderInline(rest ?? "")}
              </Box>
            </Text>
          );
        }

        const bullet = line.match(/^(\s*)[-*]\s+(.*)$/);
        if (bullet) {
          const [, indent, rest] = bullet;
          const pad = Math.min((indent?.length ?? 0) * 4, 24);
          return (
            <Text
              key={`ul-${i}`}
              size={size}
              c={color}
              component="div"
              style={{
                lineHeight: 1.55,
                paddingLeft: pad,
                display: "flex",
                gap: 8,
              }}
            >
              <Box component="span" style={{ flexShrink: 0, lineHeight: 1.55 }}>
                •
              </Box>
              <Box component="span" style={{ flex: 1 }}>
                {renderInline(rest ?? "")}
              </Box>
            </Text>
          );
        }

        return (
          <Text key={`p-${i}`} size={size} c={color} component="div" style={{ lineHeight: 1.55 }}>
            {renderInline(line)}
          </Text>
        );
      })}
    </Stack>
  );
}
